import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ItemCallback, Layout } from 'react-grid-layout';

import { deepCloneObject, deepMerge } from '@blockworks/platform';
import { BREAKPOINT_WIDTHS_BY_NAME, LayoutBreakpoint, LayoutItem } from '@blockworks/platform/api/research/studio';

import { useDashboardGridContext } from './dashboard-grid-context';

const transformToSingleColumn = (layout: LayoutItem[]) => {
    const clone = deepCloneObject(layout);
    /** sort items by their current y and x position to maintain order */
    clone.sort((a, b) => {
        if (a.y === b.y) {
            return a.x - b.x;
        }
        return a.y - b.y;
    });

    let currentY = 0;
    for (const item of clone) {
        item.x = 0;
        item.y = currentY;
        /** move the currentY down by the height of the current item */
        currentY += item.h;
    }

    return clone;
};

const getBreakpointConditions = (currentBreakpoint: LayoutBreakpoint, layoutBreakpoint: LayoutBreakpoint) => {
    const isEditing = currentBreakpoint === layoutBreakpoint;
    const isSmaller = BREAKPOINT_WIDTHS_BY_NAME[layoutBreakpoint] < BREAKPOINT_WIDTHS_BY_NAME[currentBreakpoint];

    return {
        isEditing,
        isSmaller,
    };
};

type OnWidthChange = (
    containerWidth: number,
    margin?: [number, number],
    cols?: number,
    containerPadding?: [number, number],
) => void;

const deepMergeItems = (prevLayout: LayoutItem[], nextLayout: (Layout | LayoutItem)[]) =>
    deepMerge(prevLayout, nextLayout, {
        arrayMerge: (target: LayoutItem[], source: LayoutItem[], options: deepMerge.ArrayMergeOptions) => {
            const updatedItems = target.map(item => {
                // Find the corresponding item in the source array
                const sourceItem = source.find(sItem => sItem.i === item.i);

                // If found, deep merge the target item with the source item
                if (sourceItem) {
                    return deepMerge(item, sourceItem, options);
                }

                // If not found, return the target item unchanged
                return item;
            });

            // Return the new array containing the merged items
            return updatedItems;
        },
    }) as LayoutItem[];

const useEditDashboardEvents = () => {
    const { controlledBreakpoint, layouts, setLayouts } = useDashboardGridContext();
    const [resizingItem, setResizingItem] = useState<string | undefined>();
    const [resizingItemHeight, setResizingItemHeight] = useState<number | undefined>();
    const handleOnResizeStart = useCallback<ItemCallback>((_, oldItem) => {
        setResizingItem(oldItem.i);
    }, []);

    const handleOnResizeOrDragStop = useCallback<ItemCallback>(
        newLayout => {
            const clonedLayouts = deepCloneObject(layouts);

            for (const key in clonedLayouts) {
                const breakpoint = key as LayoutBreakpoint;
                const thisBreakpoint = getBreakpointConditions(controlledBreakpoint, breakpoint);

                const updatedLayout = deepMergeItems(clonedLayouts[breakpoint as LayoutBreakpoint]!, newLayout);

                /** 👇 the breakpoint matches the current breakpoint that the user is editing */
                if (thisBreakpoint.isEditing) {
                    /** 👉 so let's update the layout*/
                    clonedLayouts[breakpoint as LayoutBreakpoint] = updatedLayout;

                    /** 👇 ensure that changes in mobile view are not reflected in the largest breakpoint */
                } else if (thisBreakpoint.isSmaller) {
                    /** 👉 transform mobile view to single column */
                    clonedLayouts[breakpoint as LayoutBreakpoint] = transformToSingleColumn(updatedLayout);
                }
            }
            clonedLayouts && setLayouts(clonedLayouts);
            setResizingItem(undefined);
            setResizingItemHeight(undefined);
        },
        [setResizingItem, setLayouts, layouts, controlledBreakpoint],
    );

    const handleOnResize = useCallback<ItemCallback>((_, __, newItem) => setResizingItemHeight(newItem.h), []);

    return { handleOnResizeOrDragStop, resizingItemHeight, handleOnResize, handleOnResizeStart, resizingItem };
};

const findMatchingBreakpoint = (num: number): LayoutBreakpoint | undefined => {
    let result: LayoutBreakpoint | undefined = undefined;
    let greatestValue = -Infinity;

    for (const [key, value] of Object.entries(BREAKPOINT_WIDTHS_BY_NAME)) {
        if (value < num && value > greatestValue) {
            greatestValue = value;
            result = key as LayoutBreakpoint;
        }
    }

    return result;
};

const getGridContainerStyles = (breakpoint: LayoutBreakpoint, isEditable?: boolean): React.CSSProperties => {
    const MAX_WIDTH_BY_BREAKPOINT = {
        lg: '100%' as const,
        base: '480px' as const,
    } satisfies Record<LayoutBreakpoint, string>;

    const width = MAX_WIDTH_BY_BREAKPOINT[breakpoint];

    if (isEditable) {
        let borderStyles = {};
        if (breakpoint === 'base') {
            borderStyles = {
                border: breakpoint === 'base' ? '4px solid var(--color-surface-divider)' : 'none',
                borderRadius: '0.5rem',
            };
        }

        return {
            ...borderStyles,
            maxWidth: width,
        };
    }
    return {};
};

const useDashboardGrid = () => {
    const { setControlledBreakpoint, isEditable, controlledBreakpoint, layouts, preloadedData } =
        useDashboardGridContext();
    const { handleOnResizeOrDragStop, resizingItemHeight, handleOnResize, resizingItem, handleOnResizeStart } =
        useEditDashboardEvents();

    const { styles, handleOnWidthChange } = useMemo(() => {
        const handleOnWidthChange: OnWidthChange = containerWidth => {
            if (isEditable) return;

            const newBreakpoint = findMatchingBreakpoint(containerWidth);

            if (!newBreakpoint) return;
            setControlledBreakpoint(newBreakpoint);
        };

        return {
            styles: getGridContainerStyles(controlledBreakpoint, isEditable),
            handleOnWidthChange,
        };
    }, [controlledBreakpoint, isEditable, setControlledBreakpoint]);

    /** let's set the initial breakpoint */
    useEffect(() => {
        /** if the user is in "edit mode", we don't need to do anything */
        if (isEditable) return;
        handleOnWidthChange(window.innerWidth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditable]);

    return {
        breakpoint: controlledBreakpoint,
        handleOnResizeOrDragStop,
        handleOnResizeStart,
        handleOnResize,
        resizingItem,
        resizingItemHeight,
        handleOnWidthChange,
        layouts,
        isEditable,
        styles,
        preloadedData,
    };
};

export { useDashboardGrid };
